import anyLeague from "../../classificators/league/anyLeague.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceForPitcher from "../../classificators/resultsSlice/resultsSliceForPitcher.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import sessionClassifierBuilder from "../../classificators/session/sessionClassifierBuilder.js";
import gameOnly from "../../classificators/sessionType/gameOnly.js";
import { IterableDataProcessor, RankingStat } from "../../entities/index.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import { PitchingRankingStats, ResultDataNew, LineupType, Lineup, ResultType } from "../../enums/index.js";

class GameBasedStatsCalculator extends MultiRankingStatCalculator {
    constructor(customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }

        super(
            [ 
                PitchingRankingStats.GamesStarted,
                PitchingRankingStats.GamesCompleted,
                PitchingRankingStats.GamesFinished,
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionClassifierBuilder(gameOnly),
                satisfiesAll(pitchingScopeContext, resultsSliceForPitcher)
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(0);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        const output = {};

        const pitcherId = parseInt(slice.scope.tagId, 10);
        const results = slice.getBiggestSlice();
        // unfortunately we cannot rely on scroing data here...
        /*
         const resultsPerTeam = this.resultsProcessor.groupData(
            results,
            [ResultDataNew.getDataPath(PitchData.BeforePlayInningIsTop.key, DataSection.Scoring.key)]
        );
        */

        const players = {
            [LineupType.Away.key]: this.getParticipants(session.lineups.find(l => l[Lineup.Type.key] === LineupType.Away.key)),
            [LineupType.Home.key]: this.getParticipants(session.lineups.find(l => l[Lineup.Type.key] === LineupType.Home.key)),
        };
        const resultsPerTeam = this.groupResultsPerTeam(results, players[LineupType.Away.key], players[[LineupType.Home.key]]);

        for (const teamType in resultsPerTeam) {
            if (!Object.hasOwnProperty.call(resultsPerTeam, teamType)) {
                continue;
            }

            const teamsResults = resultsPerTeam[teamType];
            const playersPerTeam = players[teamType];
            if (!playersPerTeam || !(playersPerTeam instanceof Array) || !playersPerTeam.includes(pitcherId)) {
                // optimizeed, so we check playersAway and playersHome if this pitcher actually played in this team before iterating
                continue;
            }

            let firstPitchById = null;
            let lastPitchById = null;
            let allPitchesById = null;

            for (let i = 0; i < teamsResults.length; i++) {
                const result = teamsResults[i];

                if (!result) {
                    continue;
                }

                const pitchedById = parseInt(result[ResultDataNew.PitcherId.key], 10);
                if (i === 0) {
                    // For Games Started
                    firstPitchById = pitchedById;
                    allPitchesById = firstPitchById;
                }
                else if (allPitchesById !== pitchedById) {
                    // for Games Completed
                    allPitchesById = null;
                }
                if (i === (teamsResults.length - 1)) {
                    // For Games Finished
                    lastPitchById = pitchedById;
                }
            }

            // https://www.mlb.com/glossary/standard-stats/games-started
            // A pitcher is credited with a game started if he is the first pitcher to throw a pitch for his team in a given game.
            if (firstPitchById && firstPitchById === pitcherId) {
                const gamesStarted = this.supportedParams[0];
                output[gamesStarted.key] = RankingStat.fromParameter(
                    gamesStarted,
                    this.numberFormatter.format(1),
                    1
                );
            }

            // https://www.mlb.com/glossary/standard-stats/complete-game
            // A pitcher earns a complete game if he pitches the entire game for his team regardless of how long it lasts.
            if (allPitchesById && allPitchesById === pitcherId) {
                const gamesCompleted = this.supportedParams[1];
                output[gamesCompleted.key] = RankingStat.fromParameter(
                    gamesCompleted,
                    this.numberFormatter.format(1),
                    1
                );
            }

            // https://www.mlb.com/glossary/standard-stats/games-finished
            // A pitcher is credited with a game finished if he is the last pitcher to pitch for his team in a given game, provided he was not the starting pitcher.
            if (lastPitchById && lastPitchById === pitcherId && firstPitchById && firstPitchById !== pitcherId) {
                const gamesFinished = this.supportedParams[2];
                output[gamesFinished.key] = RankingStat.fromParameter(
                    gamesFinished,
                    this.numberFormatter.format(1),
                    1
                );
            }
        }

        return output;
    }

    getParticipants(lineup) {
        const participants = [];
        // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/session-lineup-v2.json

        if (!lineup || typeof(lineup) !== 'object' && lineup === null) {
            return participants;
        }

        // starting order
        for (let o = 0; o < lineup[Lineup.BattingOrder.key].length; o++) {
            const lineupPlayer = lineup[Lineup.BattingOrder.key][o];
            // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/lineup-tag.json?at=feature%2Fbaseball
            participants.push(parseInt(lineupPlayer.tagID), 10);

            if (lineupPlayer.replacedFielderId) {
                // when DH is set pitcher be on bench and referenced here
                participants.push(parseInt(lineupPlayer.replacedFielderId), 10);
            }
        }

        // substitutions
        for (let s = 0; s < lineup[Lineup.Substitutions.key].length; s++) {
            const substitution = lineup[Lineup.Substitutions.key][s];
            // see https://bitbucket.org/flightscope/dto-schema/src/1aac6bd9ee03510b6a994a75476762ae703cf9a4/baseball/entity/player-substitution.json
            participants.push(parseInt(substitution.enteredTagID), 10);
        }

        // TODO - consider checking switches if app is buggy ?
        // TODO - maybe also chase over bench ?

        // https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
        return participants.filter((value, index, self) => self.indexOf(value) === index);
    }

    groupResultsPerTeam(results, playersAway, playersHome) {
        return results.reduce(
            (carry, current) => {
                // we are looking only for pitches here, hits can be easily ignored as they do not contain information about scoring
                if (!ResultType.hasPitch(current[ResultDataNew.Type.key])) {
                    return carry;
                }

                const pitcherId = parseInt(current[ResultDataNew.PitcherId.key], 10);
                if (!pitcherId) {
                    throw new Error(`Invalid pitcher id ${pitcherId} from result ${current[ResultDataNew.PitchId.key]}`);
                }
                if (playersAway.includes(pitcherId)) {
                    carry[LineupType.Away.key].push(current);
                }
                else if (playersHome.includes(pitcherId)) {
                    carry[LineupType.Home.key].push(current);
                }
                else {
                    throw new Error(`Unable to match pitcher ${pitcherId} from result ${current[ResultDataNew.PitchId.key]} to the list of players from lineup`);
                }

                return carry;
            }, {
                [LineupType.Away.key]: [],
                [LineupType.Home.key]: [],
            }
        );
    }
}

export default GameBasedStatsCalculator;